import {
  CloseIcon,
  NotificationDefaultIcon,
  NotificationErrorIcon,
  NotificationInfoIcon,
  NotificationPromoIcon,
  NotificationSuccessIcon,
} from '@repo/icons';
import { Toaster, toast } from 'sonner';
import { CountDownTimer } from '../count-down-timer';

const DURATION = 3000;

interface ToasterProviderProps {
  duration?: number;
}

const ToasterProvider = ({ duration = DURATION }: ToasterProviderProps) => (
  <Toaster
    position="top-center"
    duration={duration}
    pauseWhenPageIsHidden={false}
    className="pointer-events-auto"
  />
);

interface ToasterPayload {
  title: string;
  withLoader?: boolean;
  description?: string | string[];
  duration?: number;
  status: 'success' | 'error' | 'info' | 'promo' | 'simple' | 'default';
}

const NotificationIcons: Record<ToasterPayload['status'], React.ReactNode> = {
  error: <NotificationErrorIcon />,
  promo: <NotificationPromoIcon />,
  success: <NotificationSuccessIcon />,
  info: <NotificationInfoIcon />,
  default: <NotificationDefaultIcon />,
  simple: null,
};

const indicatorColor: Record<ToasterPayload['status'], string> = {
  success: 'var(--atoms-toast-indicator-success-fill)',
  error: 'var(--atoms-toast-indicator-error-fill)',
  info: 'var(--atoms-toast-indicator-info-fill)',
  promo: 'var(--atoms-toast-indicator-promo-fill)',
  default: 'var(--atoms-toast-indicator-default-fill)',
  simple: 'var(--atoms-toast-indicator-default-fill)',
};

const toaster = (payload: ToasterPayload) =>
  toast.custom(
    (t) => (
      <div
        className="vt-atoms-toast-comp-bg z-[200] flex min-w-[300px] items-center rounded-[4px] px-2 py-2.5 shadow-[0_4px_4px_0px_rgba(0,0,0,0.25)]"
        data-testid={`toaster-with-status-${payload.status}`}
      >
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center justify-center">{NotificationIcons[payload.status]}</div>
          <div className="ml-2 mr-5 flex w-full flex-col space-y-1 leading-tight">
            <div className="vt-font-highlight-font-family vt-font-weight-semibold vt-atoms-toast-title-text vt-text-size-medium sm:vt-text-size-medium-sm">
              {payload.title}
            </div>
            {payload.description && (
              <div className="vt-atoms-toast-desc-text vt-text-size-small-sm sm:vt-text-size-small-sm">
                {Array.isArray(payload.description) && payload.description.length > 1 ? (
                  <ul className="list-disc pl-3">
                    {payload.description.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                ) : (
                  payload.description
                )}
              </div>
            )}
          </div>
          <div className="flex h-6 w-6 items-center justify-center">
            {payload.withLoader ? (
              <CountDownTimer
                withProgressIndicator
                time={DURATION}
                size={24}
                trackWidth={3}
                indicatorWidth={3}
                indicatorColor={indicatorColor[payload.status]}
                onComplete={() => toast.dismiss(t)}
              />
            ) : (
              <div
                className="cursor-pointer"
                aria-label="Close"
                data-testid={`toaster-with-status-${payload.status}-close-button`}
                onClick={() => toast.dismiss(t)}
              >
                <CloseIcon />
              </div>
            )}
          </div>
        </div>
      </div>
    ),
    {
      duration: payload.duration ?? DURATION,
    }
  );

const clearToaster = () => {
  toast.dismiss();
};

export { ToasterProvider, toaster, clearToaster };
